import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, apiError, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { InterviewForm, SimulatorForm, today } from "@/components/AssessmentForms";
import { WorkStyleProfile } from "@/components/WorkStyleProfile";
import { BookingStatusPill } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { ArrowLeft, CheckCircle2, Circle, AlertTriangle, Phone, Mail, MessageSquare, Loader } from "lucide-react";

const SIM_DEFAULT = { date: today(), assessor: "", simulator_location: "", coordination: 3, spatial_awareness: 3, control_accuracy: 3, instruction_following: 3, concentration: 3, learning_rate: 3, response_to_correction: 3, safety_behaviour: 3, confidence_without_rushing: 3, comments: "", recommendation: "suitable" };
const INT_DEFAULT = { date: today(), assessor: "", scores: {}, red_flags: [], comments: "" };

function fmtLong(d) {
  if (!d) return "";
  return new Date(d + "T00:00:00").toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
}

function waLink(mobile) {
  const digits = (mobile || "").replace(/\D/g, "");
  if (!digits) return null;
  return `https://wa.me/${digits.startsWith("0") ? "44" + digits.slice(1) : digits}`;
}

function ProgressChip({ label, state, testid }) {
  // state: done | progress | pending
  const cls = state === "done" ? "bg-emerald-100 border-emerald-300 text-emerald-800"
    : state === "progress" ? "bg-amber-100 border-amber-300 text-amber-800"
    : "bg-elevated border-steel text-slate-500";
  return (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border text-xs font-mono uppercase tracking-widest ${cls}`} data-testid={testid}>
      {state === "done" ? <CheckCircle2 className="h-3.5 w-3.5" /> : state === "progress" ? <Loader className="h-3.5 w-3.5" /> : <Circle className="h-3.5 w-3.5" />}
      {label}{state === "progress" ? " — in progress" : state === "pending" ? " — pending" : ""}
    </span>
  );
}

export default function DayCandidate() {
  const { eventId, candidateId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const isManagement = user?.role === "management_admin";
  const [event, setEvent] = useState(null);
  const [candidate, setCandidate] = useState(null);
  const [sim, setSim] = useState(SIM_DEFAULT);
  const [interview, setInterview] = useState(INT_DEFAULT);
  const [saving, setSaving] = useState("");
  const [reportFile, setReportFile] = useState(null);
  const [outcomeReason, setOutcomeReason] = useState("");
  const [showWorkStyle, setShowWorkStyle] = useState(false);

  const load = async () => {
    try {
      const [ev, cand] = await Promise.all([
        api.get(`/admin/events/${eventId}`),
        api.get(`/staff/candidates/${candidateId}`),
      ]);
      setEvent(ev.data);
      setCandidate(cand.data);
      if (cand.data.simulator) setSim({ ...SIM_DEFAULT, ...cand.data.simulator });
      if (cand.data.interview) setInterview({ ...INT_DEFAULT, ...cand.data.interview, red_flags: cand.data.interview.red_flags || cand.data.interview.concerns || [] });
    } catch { toast.error("Could not load assessment day record"); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [eventId, candidateId]);

  const booking = useMemo(
    () => event?.attendees?.find((a) => a.candidate_id === candidateId),
    [event, candidateId]
  );

  if (!event || !candidate) return <div className="h-8 w-8 border-4 border-steel border-t-lomond rounded-full animate-spin" data-testid="day-candidate-loading" />;

  const { summary, theory } = candidate;
  const dw = candidate.weightings_day || { interview: 40, simulator: 60 };
  const interviewDone = !!candidate.interview;
  const simDone = !!candidate.simulator;
  const dayDone = interviewDone && simDone;
  const redFlags = interview.red_flags || [];
  const managementReview = redFlags.length > 0 || sim.recommendation === "not_suitable";

  const saveInterview = async () => {
    setSaving("interview");
    try {
      await api.post(`/staff/candidates/${candidateId}/interview`, interview);
      toast.success("Interview saved.");
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(""); }
  };

  const saveSim = async () => {
    setSaving("sim");
    try {
      await api.post(`/staff/candidates/${candidateId}/simulator`, sim);
      toast.success("Simulator assessment saved.");
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(""); }
  };

  const uploadReport = async () => {
    if (!reportFile) return;
    const fd = new FormData();
    fd.append("file", reportFile);
    try {
      await api.post(`/staff/candidates/${candidateId}/simulator-report`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Simulator report uploaded.");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const checkIn = async () => {
    if (!booking) return;
    try {
      await api.post(`/admin/bookings/${booking.id}/action`, { action: "mark_arrived" });
      toast.success("Marked as arrived.");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const decide = async (outcome) => {
    if (outcome !== "admit" && !outcomeReason.trim()) { toast.error("Please add a reason (audit-logged)."); return; }
    if (!window.confirm(outcome === "admit" ? "Admit this candidate to the Training Academy? They become a Trainee." : outcome === "decline" ? "Decline this candidate?" : "Place this candidate on hold for further review?")) return;
    try {
      await api.post(`/admin/bookings/${booking.id}/outcome`, { outcome, reason: outcomeReason });
      toast.success(outcome === "admit" ? "Candidate admitted — they are now a Trainee." : "Outcome recorded.");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const simPct = summary.simulator_score != null ? Math.round(summary.simulator_score / 5 * 100) : null;
  const intPct = summary.interview_score != null ? Math.round(summary.interview_score / 5 * 100) : null;
  const recommendation = managementReview ? "Management review required"
    : summary.day_score == null ? "—"
    : summary.day_score >= 80 ? "Strong — recommend admission"
    : summary.day_score >= 65 ? "Suitable"
    : summary.day_score >= 55 ? "Review / further assessment"
    : "Not recommended";

  return (
    <div className="space-y-6 max-w-5xl" data-testid="day-candidate-page">
      <div className="flex items-center gap-3 flex-wrap">
        <Button variant="outline" size="sm" onClick={() => navigate(`/staff/calendar/${eventId}`)} className="border-steel text-charcoal bg-white" data-testid="back-to-event">
          <ArrowLeft className="h-4 w-4 mr-1" /> Assessment Day
        </Button>
        <Link to={`/staff/candidates/${candidateId}`}>
          <Button variant="outline" size="sm" className="border-steel text-charcoal bg-white" data-testid="open-full-profile">Full Profile</Button>
        </Link>
      </div>

      <section className="bg-charcoal text-white rounded-md p-6" data-testid="day-candidate-header">
        <div className="flex flex-wrap justify-between gap-4">
          <div>
            <div className="hazard-stripes h-1.5 w-20 mb-3 rounded-sm" />
            <h1 className="font-heading font-extrabold uppercase tracking-tight text-xl sm:text-2xl" data-testid="day-candidate-name">{summary.name}</h1>
            <p className="text-white/70 text-sm mt-1">{fmtLong(event.date)} — {event.start_time} — {event.location_name}</p>
            <p className="text-white/60 text-xs mt-1 font-mono">{summary.email} — {candidate.profile?.mobile || "no mobile"}</p>
            <div className="flex gap-2 mt-3">
              {candidate.profile?.mobile && <a href={`tel:${candidate.profile.mobile}`}><Button size="sm" variant="outline" className="border-white/30 text-white bg-transparent h-8 text-xs" data-testid="day-call"><Phone className="h-3.5 w-3.5 mr-1" />Call</Button></a>}
              <a href={`mailto:${summary.email}`}><Button size="sm" variant="outline" className="border-white/30 text-white bg-transparent h-8 text-xs" data-testid="day-email"><Mail className="h-3.5 w-3.5 mr-1" />Email</Button></a>
              {waLink(candidate.profile?.mobile) && <a href={waLink(candidate.profile?.mobile)} target="_blank" rel="noreferrer"><Button size="sm" variant="outline" className="border-white/30 text-white bg-transparent h-8 text-xs" data-testid="day-whatsapp"><MessageSquare className="h-3.5 w-3.5 mr-1" />WhatsApp</Button></a>}
            </div>
          </div>
          <div className="text-right space-y-3">
            <div>
              <p className="font-mono text-[9px] uppercase tracking-widest text-white/50">Arrival status</p>
              <div className="mt-1 flex items-center gap-2 justify-end">
                <BookingStatusPill status={booking?.status || "booked"} />
                {booking?.status === "booked" && (
                  <Button size="sm" onClick={checkIn} className="bg-emerald-500 text-black font-bold hover:bg-emerald-400 h-8 text-xs" data-testid="day-mark-arrived">Mark Arrived</Button>
                )}
              </div>
              {booking?.check_in_time && <p className="text-[10px] font-mono text-emerald-300 mt-1" data-testid="day-checkin-time">Checked in {new Date(booking.check_in_time).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}</p>}
            </div>
            <div>
              <p className="font-mono text-[9px] uppercase tracking-widest text-white/50">Online assessment score</p>
              <p className="font-heading font-black text-3xl text-[#FFC800]" data-testid="day-theory-score">{theory ? `${theory.score_pct}%` : "—"}</p>
            </div>
          </div>
        </div>
        <div className="flex gap-2 mt-5 pt-4 border-t border-white/15 flex-wrap" data-testid="day-progress-row">
          <ProgressChip label="Interview" state={interviewDone ? "done" : "pending"} testid="progress-interview" />
          <ProgressChip label="Simulator" state={simDone ? "done" : interviewDone ? "progress" : "pending"} testid="progress-simulator" />
          <ProgressChip label="Decision" state={booking?.final_outcome ? "done" : dayDone ? "progress" : "pending"} testid="progress-decision" />
        </div>
      </section>

      {managementReview && (
        <div className="bg-red-50 border border-red-300 rounded-md p-4 flex items-start gap-3" data-testid="management-review-banner">
          <AlertTriangle className="h-5 w-5 text-red-600 shrink-0 mt-0.5" />
          <div>
            <p className="font-heading font-bold uppercase text-red-700">Management review required</p>
            <p className="text-sm text-red-600 mt-0.5">
              {redFlags.length > 0 && <>Red flags recorded: {redFlags.map((f) => f.replace(/_/g, " ")).join(", ")}. </>}
              {sim.recommendation === "not_suitable" && "Simulator rating: not suitable. "}
              A high numeric score must not override these concerns.
            </p>
          </div>
        </div>
      )}

      <section className="bg-white border border-steel rounded-md p-6" data-testid="day-interview-section">
        <h2 className="font-heading font-bold uppercase tracking-wide text-charcoal text-lg mb-4">1. Interview</h2>
        {candidate.interview_focus && (
          <div className="bg-lomond/10 border border-lomond/40 rounded-sm px-4 py-3 mb-4" data-testid="interview-focus-hint">
            <p className="text-[10px] font-mono uppercase tracking-widest text-lomond font-bold">Interview focus</p>
            <p className="text-sm text-slate-700 mt-0.5">{candidate.interview_focus}</p>
            <button type="button" onClick={() => setShowWorkStyle((v) => !v)}
              className="text-xs text-lomond underline mt-2 font-mono uppercase tracking-widest"
              data-testid="toggle-workstyle-profile-btn">
              {showWorkStyle ? "Hide work-style profile" : "View full work-style profile"}
            </button>
          </div>
        )}
        {showWorkStyle && <div className="mb-4"><WorkStyleProfile workStyle={candidate.work_style} /></div>}
        <InterviewForm value={interview} onChange={setInterview} onSave={saveInterview} saving={saving === "interview"} />
      </section>

      <section className="bg-white border border-steel rounded-md p-6" data-testid="day-simulator-section">
        <h2 className="font-heading font-bold uppercase tracking-wide text-charcoal text-lg mb-4">2. Simulator Assessment</h2>
        <SimulatorForm value={sim} onChange={setSim} onSave={saveSim} saving={saving === "sim"}
          onUploadReport={uploadReport} reportFile={reportFile} setReportFile={setReportFile} />
        {sim.report_url && <a href={fileUrl(sim.report_url)} target="_blank" rel="noreferrer" className="inline-block mt-2 text-xs text-lomond underline" data-testid="sim-report-link">View uploaded simulator report</a>}
      </section>

      <section className="bg-white border-2 border-lomond rounded-md p-6" data-testid="day-decision-section">
        <h2 className="font-heading font-bold uppercase tracking-wide text-charcoal text-lg mb-4">3. Final Decision</h2>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
          {[
            ["Online assessment", theory ? `${theory.score_pct}%` : "—", "day-score-theory"],
            [`Interview (${dw.interview}%)`, intPct != null ? `${intPct}%` : "—", "day-score-interview"],
            [`Simulator (${dw.simulator}%)`, simPct != null ? `${simPct}%` : "—", "day-score-simulator"],
            ["Assessment Day score", summary.day_score != null ? `${summary.day_score}%` : "—", "day-score-total"],
          ].map(([l, v, tid]) => (
            <div key={tid} className="bg-elevated rounded-md p-3 text-center">
              <p className="font-heading font-black text-2xl text-charcoal" data-testid={tid}>{v}</p>
              <p className="text-[9px] font-mono uppercase tracking-widest text-slate-500 mt-1">{l}</p>
            </div>
          ))}
        </div>
        <p className="text-sm mb-4" data-testid="day-recommendation">
          Overall recommendation: <span className={`font-bold ${managementReview ? "text-red-600" : "text-charcoal"}`}>{recommendation}</span>
        </p>
        {!dayDone && (
          <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 mb-4" data-testid="decision-incomplete-note">
            Complete both the interview and simulator assessments above before recording a final decision.
          </p>
        )}
        {booking?.final_outcome ? (
          <p className="bg-emerald-100 border border-emerald-300 text-emerald-800 rounded-md px-4 py-3 text-sm font-semibold" data-testid="outcome-recorded">
            Assessment day complete — outcome: {booking.final_outcome === "admit" || booking.final_outcome === "approve_trainee" ? "Admitted to Training Academy" : booking.final_outcome.replace(/_/g, " ")}
          </p>
        ) : isManagement && (
          <div className="space-y-3">
            <Textarea value={outcomeReason} onChange={(e) => setOutcomeReason(e.target.value)}
              placeholder="Reason / notes (required for decline or hold — audit-logged)"
              className="bg-white border-steel text-charcoal" data-testid="day-outcome-reason" />
            <div className="flex flex-wrap gap-2">
              <Button onClick={() => decide("admit")} disabled={!dayDone} className="bg-lomond text-black font-heading font-extrabold uppercase hover:bg-[#E6B400]" data-testid="admit-btn">
                Admit to Training Academy
              </Button>
              <Button onClick={() => decide("decline")} variant="outline" className="border-red-300 text-red-700 bg-white hover:bg-red-50" data-testid="day-decline-btn">
                Decline
              </Button>
              <Button onClick={() => decide("hold")} variant="outline" className="border-steel text-slate-600 bg-white" data-testid="day-hold-btn">
                Hold / Further Review
              </Button>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
